/**
 * Types shared by the server and the browser. dB values are rounded to 0.01 and
 * use null for "no signal" (-Infinity), because JSON has no infinity.
 */

export type StreamType = 'hls' | 'youtube' | 'srt';
export type StreamStatus = 'idle' | 'connecting' | 'live' | 'stalled' | 'ended' | 'error';
/**
 * admin: everything. operator: monitors all shared streams. user: adds and
 * monitors only their own streams (private to them and administrators), up to
 * a limit set by an administrator.
 */
export type Role = 'admin' | 'operator' | 'user';
export type Theme = 'light' | 'dark';

// ---------------------------------------------------------------------------
// Stream configuration
// ---------------------------------------------------------------------------

export type SrtMode = 'caller' | 'listener' | 'rendezvous';
export type SrtKeyLength = 0 | 16 | 24 | 32;

export interface HlsSource {
  url: string;
}

export interface YouTubeSource {
  url: string;
}

/** SRT connection as shown to clients. The passphrase itself is never sent. */
export interface SrtSourcePublic {
  mode: SrtMode;
  /** Remote host for caller and rendezvous mode. */
  host: string;
  /** Remote port (caller, rendezvous). */
  port: number;
  /** Local UDP port for listener and rendezvous mode, allocated from SRT_PORT_RANGE. */
  localPort: number | null;
  latencyMs: number;
  hasPassphrase: boolean;
  keyLength: SrtKeyLength;
  streamId: string;
  /** Destination or broadcaster this feed is a duplicate of, e.g. "BBC playout via Castr". */
  destination: string;
}

export type GroupLayout = 'mono' | 'stereo' | '5.1' | '5.0' | 'multi';

/** A named set of channels from one audio track, analysed together. */
export interface ChannelGroup {
  id: string;
  name: string;
  /** Index into the stream's audio tracks (0 = first). */
  track: number;
  /** Channel indices (0-based) within the track, in layout order. */
  channels: number[];
  layout: GroupLayout;
  /** Broadcaster preset used for this group's loudness, peak, phase and silence checks. */
  presetId: number | null;
  /** The main programme group: platform profiles are checked against it. */
  main: boolean;
}

export interface StreamConfigPublic {
  type: StreamType;
  hls?: HlsSource;
  youtube?: YouTubeSource;
  srt?: SrtSourcePublic;
  /** Channel groups; empty means automatic groups from the detected layout. */
  groups: ChannelGroup[];
  /** Presets and platform profiles this stream is checked against. */
  targetIds: number[];
  /** Decode video for interlace, black, freeze, crop and level analysis. */
  videoAnalysis: boolean;
}

export interface StreamSummary {
  id: number;
  name: string;
  type: StreamType;
  /** Short description of the source (URL, or SRT address). */
  sourceLabel: string;
  /** URL the browser can preview, if any. */
  previewUrl: string | null;
  notes: string;
  monitoring: boolean;
  status: StreamState;
  config: StreamConfigPublic;
  compliance: ComplianceSummary | null;
  /** For SRT listener mode: the address the encoder should send to. */
  listenAddress: string | null;
  /** Who added the stream; sent to administrators only. */
  owner?: { email: string; role: Role } | null;
}

export interface StreamState {
  status: StreamStatus;
  /** Plain-English detail, e.g. why the stream is in error. */
  message: string | null;
  /** Epoch ms when the current status began. */
  since: number;
  /** Epoch ms when media was last received. */
  lastDataAt: number | null;
  restarts: number;
}

// ---------------------------------------------------------------------------
// Probe
// ---------------------------------------------------------------------------

export interface ProbeAudio {
  index: number;
  codec: string;
  profile: string | null;
  sampleRate: number;
  channels: number;
  channelLayout: string | null;
  bitRate: number | null;
  language: string | null;
  title: string | null;
}

export interface ProbeVideo {
  index: number;
  codec: string;
  profile: string | null;
  level: number | null;
  width: number;
  height: number;
  sar: string | null;
  dar: string | null;
  frameRate: string | null;
  fieldOrder: string | null;
  pixFmt: string | null;
  bitDepth: number | null;
  chroma: string | null;
  colorSpace: string | null;
  colorRange: string | null;
  colorPrimaries: string | null;
  colorTransfer: string | null;
  bitRate: number | null;
  closedCaptions: boolean;
  timecode: string | null;
}

export interface ProbeSubtitle {
  index: number;
  codec: string;
  language: string | null;
}

export interface ProbeInfo {
  at: number;
  formatName: string | null;
  /** Seconds, null for live sources. */
  duration: number | null;
  bitRate: number | null;
  audio: ProbeAudio[];
  video: ProbeVideo[];
  subtitles: ProbeSubtitle[];
  timecode: string | null;
}

// ---------------------------------------------------------------------------
// Real-time metering (about 10 Hz)
// ---------------------------------------------------------------------------

export interface LoudnessValues {
  momentary: number | null;
  shortTerm: number | null;
  integrated: number | null;
  lra: number | null;
  maxMomentary: number | null;
  maxShortTerm: number | null;
  /** Maximum true peak across the group's channels since reset, dBTP. */
  maxTruePeak: number | null;
  /** Seconds integrated since the last reset. */
  elapsed: number;
}

export interface StereoValues {
  /** Phase correlation over the block, -1..+1; null when silent. */
  correlation: number | null;
  /** Right minus left short-term loudness in dB; positive = right louder. */
  balance: number | null;
  /** Mono-sum short-term loudness minus stereo short-term loudness, dB (0 = fully mono compatible). */
  monoDelta: number | null;
}

export interface TrackMeters {
  track: number;
  channels: number;
  layout: string | null;
  labels: string[];
  /** Per channel, maximum over the block, dBFS. */
  samplePeak: (number | null)[];
  /** Per channel, maximum over the block, dBTP. */
  truePeak: (number | null)[];
  /** Per channel, PPM (IEC 60268-10 Type II) reading, dBFS. */
  ppm: (number | null)[];
  rms: (number | null)[];
  /** Per channel, maximum true peak since reset, dBTP. */
  truePeakMax: (number | null)[];
  /** Per channel, clip events since reset. */
  clipCount: number[];
  /** Per channel DC offset, as a percentage of full scale. */
  dc: number[];
}

export interface GroupMeters {
  id: string;
  name: string;
  main: boolean;
  layout: GroupLayout;
  track: number;
  channels: number[];
  loudness: LoudnessValues;
  stereo: StereoValues | null;
}

export interface MeterFrame {
  type: 'meter';
  streamId: number;
  /** Server epoch ms. */
  t: number;
  tracks: TrackMeters[];
  groups: GroupMeters[];
}

/** Centre frequencies (Hz) of the 1/3-octave bands in SpectrumMessage.thirdOctave. */
export const THIRD_OCTAVE_CENTRES = [
  20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500, 630, 800, 1000, 1250, 1600, 2000, 2500,
  3150, 4000, 5000, 6300, 8000, 10000, 12500, 16000, 20000,
];
/** Number of logarithmically spaced FFT bins (20 Hz to 20 kHz) in SpectrumMessage.fft. */
export const FFT_LOG_BINS = 120;

export function fftLogBinFrequency(i: number): number {
  return 20 * Math.pow(1000, (i + 0.5) / FFT_LOG_BINS);
}

/** Spectrum of each group (power average of its channels), about 5 Hz. */
export interface SpectrumMessage {
  type: 'spectrum';
  streamId: number;
  t: number;
  groups: { id: string; thirdOctave: (number | null)[]; fft: (number | null)[] }[];
}

// ---------------------------------------------------------------------------
// Video, transport (1 Hz)
// ---------------------------------------------------------------------------

export interface VideoEvent {
  kind: 'black' | 'freeze';
  start: number;
  end: number | null;
}

export interface VideoStats {
  /** Measured frame rate over the last few seconds. */
  fps: number | null;
  /** Nominal frame rate from the stream (e.g. "25/1"). */
  nominalFps: string | null;
  /** Coefficient of variation of frame durations; above ~0.05 suggests variable frame rate. */
  frameDurationCv: number | null;
  /** Frames missing according to timestamps, total since connect. */
  droppedFrames: number;
  /** Frames with repeated timestamps, total since connect. */
  duplicatedFrames: number;
  /** Seconds between the last two keyframes. */
  keyframeInterval: number | null;
  keyframeIntervalMin: number | null;
  keyframeIntervalMax: number | null;
  /** Seconds since the last keyframe. */
  sinceKeyframe: number | null;
  /** Video bitrate over the last second, bits/s. */
  bitrate: number | null;
  /** Average over the last 30 s. */
  bitrateAvg: number | null;
  bitrateMin: number | null;
  bitrateMax: number | null;
  /** Coefficient of variation of 1 s bitrates over 30 s (CBR is typically below 0.15). */
  bitrateCv: number | null;
  /** Decoded-video analysis; null when video analysis is off. */
  analysis: VideoAnalysis | null;
  events: VideoEvent[];
}

export interface VideoAnalysis {
  /** idet multi-frame classification counts over the last 10 s of frames. */
  idet: { tff: number; bff: number; progressive: number; undetermined: number };
  detectedScan: 'progressive' | 'interlaced' | 'mixed' | 'unknown';
  detectedFieldOrder: 'tff' | 'bff' | 'mixed' | null;
  /** Share of frames (0..1) the single-frame detector saw as interlaced: a combing indicator. */
  combing: number;
  black: boolean;
  frozen: boolean;
  /** Active picture area from cropdetect. */
  crop: { width: number; height: number; x: number; y: number } | null;
  /**
   * Luma statistics (8-bit code values) over the last second: extremes, mean, and
   * the lowest 10th / highest 90th percentile across frames (signalstats YLOW/YHIGH).
   */
  luma: { min: number; max: number; avg: number; low: number; high: number } | null;
  chroma: { min: number; max: number } | null;
  width: number | null;
  height: number | null;
}

export interface HlsHealth {
  playlistUrl: string;
  targetDuration: number | null;
  /** Seconds since the playlist last gained a segment. */
  sinceNewSegment: number | null;
  lastSegmentDuration: number | null;
  /** Segments longer than the target duration (count over the session). */
  overlongSegments: number;
  /** Media-sequence gaps (segments that were never listed). */
  missingSegments: number;
  /** Segments that arrived later than 1.5x the target duration. */
  lateSegments: number;
  playlistErrors: number;
  lastError: string | null;
  /** Seconds from the end of the newest segment to now (EXT-X-PROGRAM-DATE-TIME), if present. */
  latency: number | null;
  /** Playlist refresh interval actually observed, seconds. */
  refreshInterval: number | null;
  endList: boolean;
}

export interface SrtStats {
  connected: boolean;
  rttMs: number | null;
  bandwidthMbps: number | null;
  receiveMbps: number | null;
  /** Counters for the last second. */
  packetsReceived: number;
  packetsLost: number;
  packetsRetransmitted: number;
  packetsDropped: number;
  /** Totals since connect. */
  totalReceived: number;
  totalLost: number;
  totalDropped: number;
  totalRetransmitted: number;
  /** Loss in the last 10 s as a percentage of packets. */
  lossPercent: number;
  bufferMs: number | null;
  latencyMs: number | null;
  flightSize: number | null;
  /** Times the connection dropped since monitoring started. */
  disconnects: number;
}

export interface TsPid {
  pid: number;
  description: string;
  bitrate: number;
  kind: 'video' | 'audio' | 'subtitles' | 'teletext' | 'scte35' | 'table' | 'data' | 'other';
}

export interface TsHealth {
  bitrate: number | null;
  services: { id: number; name: string; provider: string; pmtPid: number; pcrPid: number; pids: number[] }[];
  pids: TsPid[];
  /** TR 101 290 indicator counters over the session. */
  counters: {
    syncErrors: number;
    continuityErrors: number;
    transportErrors: number;
    patErrors: number;
    pmtErrors: number;
    pidErrors: number;
    pcrDiscontinuities: number;
    pcrRepetitionErrors: number;
    ptsErrors: number;
  };
  /** Counters in the last analysis interval. */
  recent: TsHealth['counters'];
  scte35: { present: boolean; events: number; lastAt: number | null };
  captions: { cea608: boolean; dvbSubtitles: boolean; teletext: boolean };
  updatedAt: number;
}

export interface SourceDetails {
  type: StreamType;
  live: boolean;
  youtube?: { title: string; channel: string; liveStatus: string; height: number | null; videoId: string | null };
}

export interface TransportMessage {
  type: 'transport';
  streamId: number;
  t: number;
  source: SourceDetails | null;
  hls: HlsHealth | null;
  srt: SrtStats | null;
  ts: TsHealth | null;
}

export interface VideoMessage {
  type: 'video';
  streamId: number;
  t: number;
  video: VideoStats;
}

// ---------------------------------------------------------------------------
// Findings, compliance, alerts
// ---------------------------------------------------------------------------

export type Severity = 'PASS' | 'INFO' | 'WARNING' | 'FAIL';
export type Confidence = 'definite' | 'likely' | 'possible';

export interface Evidence {
  label: string;
  value: string;
}

export interface Finding {
  id: number;
  streamId: number;
  ruleId: string;
  key: string;
  severity: Severity;
  category: 'audio' | 'video' | 'transport';
  title: string;
  explanation: string;
  /** Short phrase used in the summary paragraph. */
  phrase: string;
  evidence: Evidence[];
  likelyCauses: string[];
  suggestedFix: string;
  confidence: Confidence;
  targetId: number | null;
  groupId: string | null;
  startedAt: number;
  updatedAt: number;
  resolvedAt: number | null;
}

export interface TargetStatus {
  targetId: number;
  name: string;
  kind: 'broadcaster' | 'platform';
  status: Severity | 'UNKNOWN';
}

export interface ComplianceSummary {
  overall: Severity | 'UNKNOWN';
  targets: TargetStatus[];
  /** Generated from the findings by the rules engine (source of truth). */
  paragraph: string;
  /** Optional natural-language restatement by an LLM (setting, off by default). */
  llmParagraph: string | null;
  updatedAt: number;
}

export interface FindingsMessage {
  type: 'findings';
  streamId: number;
  findings: Finding[];
  summary: ComplianceSummary;
}

export type AlertType = 'true_peak' | 'short_term' | 'silence' | 'phase' | 'srt_loss' | 'stalled' | 'new_fail';

export interface Alert {
  id: number;
  streamId: number;
  streamName: string;
  type: AlertType;
  severity: 'WARNING' | 'FAIL';
  message: string;
  at: number;
  acknowledgedAt: number | null;
  acknowledgedBy: string | null;
}

export interface AlertMessage {
  type: 'alert';
  alert: Alert;
}

export interface StatusMessage {
  type: 'status';
  streamId: number;
  state: StreamState;
}

export interface ProbeMessage {
  type: 'probe';
  streamId: number;
  probe: ProbeInfo;
}

export type ServerMessage =
  | MeterFrame
  | SpectrumMessage
  | StatusMessage
  | ProbeMessage
  | TransportMessage
  | VideoMessage
  | FindingsMessage
  | AlertMessage
  | { type: 'error'; message: string };

export interface PairRef {
  track: number;
  a: number;
  b: number;
}

export type ClientMessage =
  | { type: 'subscribe'; streamId: number; pair?: PairRef }
  | { type: 'unsubscribe'; streamId: number }
  | { type: 'watchStatus' };

/**
 * Binary frame carrying decimated sample pairs for the goniometer:
 *   u8  kind (= BINARY_KIND_PAIRS)
 *   u8  channel A index
 *   u8  channel B index
 *   u8  track index
 *   u32 stream id (little endian)
 *   u32 pair sample rate in Hz (little endian)
 *   i16 interleaved A,B samples (little endian, full scale = 32767)
 */
export const BINARY_KIND_PAIRS = 1;
export const PAIRS_HEADER_BYTES = 12;
export const PAIR_DECIMATION = 8; // 48 kHz / 8 = 6000 pairs per second

export interface HistoryResponse {
  /** Epoch ms of the first point. */
  start: number;
  /** Milliseconds between points. */
  step: number;
  momentary: (number | null)[];
  shortTerm: (number | null)[];
}

export interface TransportHistoryPoint {
  t: number;
  rttMs: number | null;
  lossPercent: number | null;
  receiveMbps: number | null;
  bufferMs: number | null;
  hlsLatency: number | null;
  sinceNewSegment: number | null;
  videoBitrate: number | null;
}

// ---------------------------------------------------------------------------
// Presets and platform profiles
// ---------------------------------------------------------------------------

export interface LoudnessSpec {
  integrated: number;
  /** Allowed deviation for live programmes, LU. */
  tolerance: number;
  maxTruePeak: number;
  maxShortTerm: number | null;
  maxLra: number | null;
}

export interface VideoFormatSpec {
  width: number;
  height: number;
  /** Frame rates in frames per second (e.g. 25, 50, 29.97). */
  frameRates: number[];
  /** Minimum and maximum recommended video bitrate, kbit/s. */
  bitrateMinKbps: number | null;
  bitrateMaxKbps: number | null;
}

export interface TargetSpec {
  loudness: LoudnessSpec | null;
  silence: { thresholdDb: number; seconds: number };
  phase: { correlationThreshold: number; seconds: number };
  audio: {
    codecs: string[] | null;
    sampleRates: number[] | null;
    channels: number[] | null;
    trackCount: number | null;
    bitrateMinKbps: number | null;
    bitrateMaxKbps: number | null;
  };
  video: {
    codecs: string[] | null;
    profiles: string[] | null;
    formats: VideoFormatSpec[] | null;
    scan: 'progressive' | 'interlaced' | null;
    fieldOrder: 'tff' | 'bff' | null;
    colorSpace: string | null;
    colorRange: 'tv' | 'pc' | null;
    /** Maximum keyframe interval, seconds. */
    keyframeIntervalMax: number | null;
    rateControl: 'CBR' | 'VBR' | null;
  };
  srt: { maxLossPercent: number; maxRttMs: number; minBufferMs: number } | null;
}

export interface Target {
  id: number;
  kind: 'broadcaster' | 'platform';
  name: string;
  description: string;
  spec: TargetSpec;
  /** Where the requirements come from. */
  sourceUrls: string[];
  /** Date the published requirements were last checked (YYYY-MM-DD). */
  checkedAt: string | null;
  builtin: boolean;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// Sessions and reports
// ---------------------------------------------------------------------------

export interface MonitoringSession {
  id: number;
  streamId: number;
  startedAt: number;
  endedAt: number | null;
}
